import { Collapsible as CollapsibleRoot, CollapsibleContent, CollapsibleTrigger } from '@radix-ui/react-collapsible'
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react'
import { useEffect, useState } from 'react'

import { usePatchMembersInstructionsInstructionId } from 'utils/web_api'
import { type MembersInstruction, type MembersInstructionListMetadata, type MembersInstructionStatus } from 'utils/web_api.schemas'

import Instruction from './Instruction'
import { ProgressDessert } from '../ProgressDessert/ProgressDessert'

type CompletedInstructionsSectionProps = {
  completedInstructions: MembersInstruction[]
  onInstructionChange: (instructionId: string, checked: boolean) => void
}

const trackInstructionToggle = (
  instructionId: string,
  checked: boolean,
  updatedInstructions: MembersInstruction[]
) => {
  // Calculate updated instruction lists for tracking
  const updatedPendingInstructions = updatedInstructions
    .filter((instruction) => instruction.status === 'pending')
    .map((instruction) => instruction.title)
  const updatedCompletedInstructions = updatedInstructions
    .filter((instruction) => instruction.status === 'completed')
    .map((instruction) => instruction.title)

  // Get the instruction title for human-readable tracking
  const toggledInstruction = updatedInstructions.find((instruction) => instruction.id === instructionId)

  window.segment?.track('[EE] Instruction toggled', {
    referrer_page: document.referrer,
    instruction_id: instructionId,
    instruction_title: toggledInstruction?.title,
    checked,
    num_tasks_total: updatedInstructions.length,
    num_tasks_completed: updatedCompletedInstructions.length,
    pending_instructions: updatedPendingInstructions,
    completed_instructions: updatedCompletedInstructions,
  })
}

// === Pending Instructions Section ===
type PendingInstructionsSectionProps = {
  pendingInstructions: MembersInstruction[]
  metadata?: MembersInstructionListMetadata
  hasMultiplePlanYears: boolean
  onInstructionChange: (instructionId: string, checked: boolean) => void
}

const PendingInstructionsSection = ({ pendingInstructions, metadata, hasMultiplePlanYears, onInstructionChange }: PendingInstructionsSectionProps) => {
  if (pendingInstructions.length === 0) {
    return null
  }

  // Group pending instructions by plan year (only when multiple plan years exist)
  const planYears = (metadata?.plan_years as number[] | undefined) || []

  const groupPendingInstructionsByPlanYear = () => {
    const grouped: Record<number, MembersInstruction[]> = {}
    for (const instruction of pendingInstructions) {
      const planYear = instruction.plan_year
      if (planYear !== undefined) {
        if (!grouped[planYear]) {
          grouped[planYear] = []
        }
        grouped[planYear].push(instruction)
      } else {
        // Handle instructions without plan_year
        if (!grouped[0]) {
          grouped[0] = []
        }
        grouped[0].push(instruction)
      }
    }
    return grouped
  }

  const pendingByPlanYear = hasMultiplePlanYears ? groupPendingInstructionsByPlanYear() : {}

  if (hasMultiplePlanYears) {
    // Multiple plan years: group pending instructions by plan year with headers
    return (
      <div className='flex flex-col gap-6 mt-4'>
        {planYears.map((planYear: number) => {
          const sectionHeader = planYear > 0 ? `For your ${planYear} plans:` : ''

          const pendingInstructionsForYear = pendingByPlanYear[planYear] || []
          if (pendingInstructionsForYear.length === 0) {
            return null
          }

          return (
            <div key={planYear} className='flex flex-col gap-2'>
              {/* Plan year header */}
              <p className='body text-gray-500'>{sectionHeader}</p>
              {/* Instructions list */}
              <div className='flex flex-col gap-2'>
                {pendingInstructionsForYear.map((instruction) => (
                  <Instruction key={instruction.id} {...instruction} onChange={onInstructionChange} />
                ))}
              </div>
            </div>
          )
        })}
      </div>
    )
  }

  // Single plan year or no plan years: show pending instructions without headers
  return (
    <div className='flex flex-col gap-2 mt-4'>
      {pendingInstructions.map((instruction) => (
        <Instruction key={instruction.id} {...instruction} onChange={onInstructionChange} />
      ))}
    </div>
  )
}

// === Completed Instructions Section ===
const CompletedInstructionsSection = ({ completedInstructions, onInstructionChange }: CompletedInstructionsSectionProps) => {
  const [isOpen, setIsOpen] = useState(false)

  if (completedInstructions.length === 0) {
    return null
  }

  const heading = `${completedInstructions.length} completed ${completedInstructions.length > 1 ? 'tasks' : 'task'}`

  return (
    <CollapsibleRoot
      open={isOpen}
      onOpenChange={setIsOpen}
      data-testid='completed-instructions-section'
    >
      {/* Heading */}
      <CollapsibleTrigger className='flex justify-start mt-4 items-center gap-2 body-bold text-gray-500'>
        {heading}
        {isOpen ? <ChevronUpIcon aria-hidden size={16} /> : <ChevronDownIcon aria-hidden size={16} />}
      </CollapsibleTrigger>
      {/* Instructions list */}
      <CollapsibleContent className='mt-2'>
        <div className='flex flex-col gap-2'>
          {completedInstructions.map((instruction) => (
            <Instruction
              key={instruction.id}
              {...instruction}
              onChange={onInstructionChange}
              isInCompletedSection
            />
          ))}
        </div>
      </CollapsibleContent>
    </CollapsibleRoot>
  )
}

// === Instructions Section ===
const InstructionsSection = ({ instructions, metadata }: { instructions: MembersInstruction[]; metadata?: MembersInstructionListMetadata }) => {
  const [localInstructions, setLocalInstructions] = useState(instructions)

  const pendingInstructions = localInstructions.filter((instruction) => instruction.status === 'pending')
  const completedInstructions = localInstructions.filter((instruction) => instruction.status === 'completed')
  const numTasksCompleted = completedInstructions.length

  const hasMultiplePlanYears = ((metadata?.plan_years as number[] | undefined) || []).length > 1

  const numTasksTotal = instructions.length
  const tasksCompletedText = `${numTasksCompleted} of ${numTasksTotal} tasks completed`

  useEffect(() => {
    const pendingTitles = pendingInstructions.map((instruction) => instruction.title)
    const completedTitles = completedInstructions.map((instruction) => instruction.title)

    window.segment?.page('[EE] Instructions section viewed', {
      num_tasks_total: numTasksTotal,
      num_tasks_completed: numTasksCompleted,
      pending_instructions: pendingTitles,
      completed_instructions: completedTitles,
    })
  }, [])

  // Hook to update instruction status
  const { mutate: updateInstruction } = usePatchMembersInstructionsInstructionId()

  const handleInstructionChange = (instructionId: string, checked: boolean) => {
    const newStatus: MembersInstructionStatus = checked ? 'completed' : 'pending'

    // Call PATCH /instructions/:instructionId to update the instruction status
    updateInstruction(
      {
        instructionId,
        data: {
          status: newStatus,
        },
      },
      {
        onSuccess: () => {
          setLocalInstructions((prev) => {
            const updatedInstructions = prev.map((instruction) =>
              instruction.id === instructionId ? { ...instruction, status: newStatus } : instruction
            )
            trackInstructionToggle(instructionId, checked, updatedInstructions)
            return updatedInstructions
          })
        }
      }
    )
  }

  const headingText = hasMultiplePlanYears ? "Here's what's on your plate" : "Here's what's on your plate:"

  // === Renders Instructions Section ===
  return (
    numTasksTotal > 0 && (
      <div className='p-4 border-emphasis shadow-emphasis rounded-2xl bg-white'>
        {/* Header */}
        <div className='flex justify-between'>
          {/* Header - left */}
          <h1 className='heading-sm flex items-center'>{headingText}</h1>
          {/* Header - right */}
          <div className='flex items-center gap-x-4'>
            {tasksCompletedText}
            <ProgressDessert
              completed={numTasksCompleted}
              total={numTasksTotal}
              size={32}
              strokeWidth={6}
            />
          </div>
        </div>
        {/* List of instructions */}
        {/* Pending instructions */}
        <PendingInstructionsSection
          pendingInstructions={pendingInstructions}
          metadata={metadata}
          hasMultiplePlanYears={hasMultiplePlanYears}
          onInstructionChange={handleInstructionChange}
        />
        {/* Completed instructions */}
        {completedInstructions.length > 0 && (
          <CompletedInstructionsSection
            completedInstructions={completedInstructions}
            onInstructionChange={handleInstructionChange}
          />
        )}
      </div>
    )
  )
}

export default InstructionsSection
